import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectionList } from '@angular/material/list';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { fadeInAndOut } from '~lmo/animations';
import { Mesh } from '~lmo/models/mesh.model';
import { isDriverRejected, isPending, Order } from '~lmo/models/order.model';
import { loadPluralMapping } from '~lmo/pluralMappings';
import { LmoOrdersService } from '~lmo/services';
import { HelperService } from '~services/helper.service';
import { UserService } from '~services/user.service';
import { LmoOrdersCancelDialogComponent } from '../lmo-orders-cancel-dialog/lmo-orders-cancel-dialog.component';

interface MeshWithOrders extends Mesh {
  orders: Order[];
}

@Component({
  animations: [fadeInAndOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-pending',
  styleUrls: ['./lmo-orders-pending.component.scss'],
  templateUrl: './lmo-orders-pending.component.html',
})
export class LmoOrdersPendingComponent implements OnInit {
  public loadPluralMapping = loadPluralMapping;
  public isPending = isPending;
  public cancelling$ = new BehaviorSubject<Boolean>(false);
  public isDriverRejected = isDriverRejected;
  public pendingOrders$: Observable<MeshWithOrders[]>;
  @ViewChild('selectedOrderIds', { static: false }) private selectedOrders: MatSelectionList;

  constructor(
    public orderService: LmoOrdersService,
    private matDialog: MatDialog,
    public helperService: HelperService,
    private usersService: UserService,
  ) {}

  public ngOnInit() {
    this.pendingOrders$ = this.orderService.pendingOrders$.pipe(map(groupByMeshes));
  }

  public trackByIdFn(_index: number, order: { id: number }) {
    return order.id;
  }

  public isVorto() {
    return this.usersService.isVortoUser() || this.usersService.isShaleappsEmail();
  }

  public cancelOrders() {
    const canceller = this.matDialog.open(LmoOrdersCancelDialogComponent);
    canceller.componentInstance.orderIds = this.selectedOrders.selectedOptions.selected.map(
      (option) => option.value.id,
    );
    canceller.afterClosed().subscribe((wasCancelled) => {
      this.cancelling$.next(true);
      if (wasCancelled) {
        this.selectedOrders.deselectAll();
      }
      this.cancelling$.next(false);
    });
  }

  public toggleMeshChecks(checked: MatCheckboxChange, meshId: number) {
    const meshMatches = this.selectedOrders.options.filter((option) => {
      const order = option.value as Order;
      return order.mesh.id === meshId;
    });
    meshMatches.forEach((option) => {
      if (option.selected !== checked.checked) {
        option.toggle();
      }
    });
  }
}

function groupByMeshes(orders: Order[]): MeshWithOrders[] {
  const meshMap: Record<number, MeshWithOrders> = {};
  orders.forEach((order) => {
    if (!meshMap[order.mesh.id]) {
      meshMap[order.mesh.id] = {
        ...order.mesh,
        orders: [],
      };
    }
    meshMap[order.mesh.id].orders.push(order);
  });
  const meshWithOrdersArray: MeshWithOrders[] = Object.keys(meshMap).map((key) => meshMap[key]);
  meshWithOrdersArray.sort((m1, m2) => (m1.preferredOrder < m2.preferredOrder ? -1 : 1));
  meshWithOrdersArray.forEach((meshWithOrders) => {
    meshWithOrders.orders.sort((o1, o2) => (o1.loadNumber < o2.loadNumber ? -1 : 1));
  });
  return meshWithOrdersArray;
}
